import { createSlice } from "@reduxjs/toolkit";
import {
  getAccessRights,
  getCompanies,
  getCompanyPlanInfo,
  getNodesWithWhiteLabel,
  getBranchListByProduct,
  getProductList,
  getSnFormatsByBranch,
  checkSnAssignAvailability,
  validateUnassignUrl,
  validateUnassignRange,
  getBundleOptions,
  getCompanyFeature,
  setCompanyBundle,
} from "../action/companies.action";
import { createBundle } from "../../../bundles/redux/bundles.action";
import { findBundleById } from "../../../bundles/utils/bundle-modules";

const COMPANIES = "companies";
const initialState = {
  totalCompanies: 0,
  companiesPage: 0,
  companies: [],
  accessRights: {
    list: [],
    selectedIds: [],
    isError: false,
  },
  bundleOptions: [],
  // Whether bundle-options has ever come back. An empty `bundleOptions` means two completely
  // different things — "not fetched yet / the GET failed" and "fetched, and the system really
  // has no live bundles" — and a panel that cannot tell them apart either renders a skeleton
  // for ever or asserts that every bundle is retired. Length cannot answer it; this can.
  bundleOptionsLoaded: false,
  // The dropdown read failed and no list has ever come back. Same reason as
  // bundles.moduleListingFailed: without it the editors cannot tell "not back yet" from "it
  // failed", and a skeleton that never resolves is the result. Once a list HAS landed a
  // later failed refresh does not raise this — the cached list is still worth showing.
  bundleOptionsFailed: false,
  companyFeature: null,
  // Which company `companyFeature` is about. There is ONE slot but several surfaces (the
  // details dialog's Company Features tab, the standalone bundle dialog), and both of their
  // containers stay mounted for the life of the page — so a response for company A can land
  // after the admin has moved on to company B. Without this the panel would re-seed from A's
  // bundle and the next Save would write A's bundle onto B.
  companyFeatureId: null,
  // Why the last read of that slot failed, or null. The panel renders a spinner while it has
  // no company feature to seed from, so "the read failed" has to be distinguishable from
  // "the read has not come back yet" — otherwise a rejected read is a spinner that never
  // resolves, with no way out and nothing on screen saying so.
  companyFeatureError: null,
  planInfo: null,
  nodesWithWhiteLabel: null,
  totalCompanyManageBranch: 0,
  companyManageBranchPage: 0,
  allBranchWhiteLabelCount: [],
  branchDropdown: {
    list: [],
    isError: false,
  },
  productDropdown: {
    list: [],
    totalRecords: 0,
    totalFiltered: 0,
    hasNextPage: true,
    isError: false,
  },
  snFormats: {
    list: [],
    isError: false,
  },
  reassignResult: {
    total_assigned: 0,
    total_reassigned: 0,
    failed_validate: [],
    isError: false,
  },
  snAvailabilityList: {
    data: [],
    isForceReassign: false,
    isReady: false,
    isError: false,
    isSNexceedRange: false,
  },
  unassignV2Validation: {
    urlResults: null,
    rangeResults: null,
    isValidating: false,
    isError: false,
  },
};
const companiesSlice = createSlice({
  name: COMPANIES,
  initialState,
  reducers: {
    clearCompanies: (state, action) => {
      state.totalCompanies = 0;
      state.companies = [];
    },
    addBranchWhiteLabelCount: (state, action) => {
      state.allBranchWhiteLabelCount[action.payload] += 1;
    },
    minusBranchWhiteLabelCount: (state, action) => {
      state.allBranchWhiteLabelCount[action.payload] -= 1;
    },
    resetBranchDropdown: (state) => {
      state.branchDropdown = initialState.branchDropdown;
    },
    resetProductDropdown: (state) => {
      state.productDropdown = initialState.productDropdown;
    },
    setReassignResult: (state, action) => {
      state.reassignResult = action.payload;
    },
    clearReassignResult: (state, action) => {
      state.reassignResult = initialState.reassignResult;
    },
    resetSNAvailabilityList(state) {
      state.snAvailabilityList = initialState.snAvailabilityList;
    },
    resetUnassignV2Validation(state) {
      state.unassignV2Validation = initialState.unassignV2Validation;
    },
    // Dropping the claim on the slot as well as its contents: any getCompanyFeature already
    // in flight will no longer match on arrival and is discarded.
    clearCompanyFeature(state) {
      state.companyFeature = initialState.companyFeature;
      state.companyFeatureId = initialState.companyFeatureId;
      state.companyFeatureError = initialState.companyFeatureError;
    },
  },
  extraReducers: {
    [getCompanies.fulfilled]: (state, action) => {
      state.totalCompanies = action.payload.recordsFiltered;
      state.companiesPage = action.meta.arg.page;
      state.allBranchWhiteLabelCount = [];
      const processCompanies = action.payload.data.map((company, index) => {
        const processCompany = {};
        processCompany.company = {
          row: index,
          id: company.id || "",
          logo: company.image_logo_url || "",
          whiteLabel: company.white_label || "-",
          name: company.name || "",
          regNo: company.reg_no || "",
          industry: company.industry || "",
          email: company.email || "",
          contact: company.contact || "",
          addressLine1: company.line1 || "",
          addressLine2: company.contact || "",
          postcode: company.postal_code || "",
          state: company.state || "",
          city: company.city || "",
          country: company.country || "",
          status: company.status || "",
          bundle: company.bundle,
          // deprecated alias of `bundle`, kept for un-updated consumers
          feature_group: company.feature_group,
          plan: company.plan_name || "",
          expired_at: company.expired_at || "",
          deactive_at: company.deactive_at || "",
          sales_email: company.sales_email || "",
          api_access_key: company.api_access_key || null,
          api_access_secret: company.api_access_secret || null,
        };
        if (company.article_assoclation) {
          processCompany.company.articleOfAssoclationUrl =
            company.article_assoclation.url || "";
        }
        if (company.director_identification) {
          processCompany.company.directorIdentificationUrl =
            company.director_identification.url || "";
        }
        if (company.owner) {
          processCompany.owner = {
            firstName: company.owner.first_name || "",
            lastName: company.owner.last_name || "",
            contact: company.owner.phone_number || "",
            email: company.owner.email || "",
            timezone: company.owner.timezone || "",
          };
        } else {
          processCompany.owner = {
            firstName: "",
            lastName: "",
            contact: "",
            email: "",
            timezone: "",
          };
        }
        state.allBranchWhiteLabelCount.push(company.branch_white_label_count);
        return processCompany;
      });
      state.companies = processCompanies;
    },
    [getAccessRights.pending]: (state, action) => {
      state.accessRights = initialState.accessRights;
    },
    [getAccessRights.fulfilled]: (state, action) => {
      const { payload } = action;
      const ids = [];
      payload.forEach((group) => {
        group.subgroup.forEach((s) => {
          s.details.forEach((d) => {
            if (d.is_enabled) {
              ids.push(d.id);
            }
          });
        });
      });
      state.accessRights.list = payload;
      state.accessRights.selectedIds = ids;
    },
    [getAccessRights.rejected]: (state, action) => {
      state.accessRights.isError = true;
    },
    [getCompanyPlanInfo.pending]: (state, action) => {
      state.planInfo = initialState.planInfo;
    },
    [getCompanyPlanInfo.fulfilled]: (state, action) => {
      state.planInfo = action.payload;
    },
    [getNodesWithWhiteLabel.pending]: (state, action) => {
      state.nodesWithWhiteLabel = initialState.nodesWithWhiteLabel;
    },
    [getNodesWithWhiteLabel.fulfilled]: (state, action) => {
      state.nodesWithWhiteLabel = action.payload.data;
      state.totalCompanyManageBranch = action.payload.recordsTotal;
      state.companyManageBranchPage = action.meta.arg.page;
    },
    [getNodesWithWhiteLabel.rejected]: (state, action) => {
      state.nodesWithWhiteLabel.isError = true;
    },
    [getBranchListByProduct.pending]: (state) => {
      state.branchDropdown = initialState.branchDropdown;
    },
    [getBranchListByProduct.fulfilled]: (state, action) => {
      state.branchDropdown.list = action.payload;
    },
    [getBranchListByProduct.rejected]: (state) => {
      state.branchDropdown.isError = true;
    },
    // [getProductList.pending]: (state) => {
    //   state.branchDropdown = initialState.branchDropdown;
    // },
    [getProductList.fulfilled]: (state, action) => {
      const { payload } = action;
      if (state.productDropdown.hasNextPage) {
        state.productDropdown.list = [
          ...state.productDropdown.list,
          ...payload.data,
        ];
      }
      state.productDropdown.totalRecords = payload.recordsTotal;
      state.productDropdown.totalFiltered = payload.recordsFiltered;
      state.productDropdown.hasNextPage =
        state.productDropdown.list.length < payload.recordsFiltered;
    },
    [getProductList.rejected]: (state) => {
      state.productDropdown.isError = true;
    },
    [getSnFormatsByBranch.rejected]: (state) => {
      state.snFormats = initialState.snFormats;
    },
    [getSnFormatsByBranch.fulfilled]: (state, action) => {
      const { payload } = action;

      state.snFormats.list = payload.map((e) => {
        const [prefix, length, postfix, generatingType] = e.format.split(",");

        return {
          format: e.format,
          prefix,
          length,
          postfix,
          generatingType,
          quantity: e.total_quantity || 0,
        };
      });
    },
    [getSnFormatsByBranch.rejected]: (state) => {
      state.snFormats.isError = true;
    },
    [checkSnAssignAvailability.pending]: (state) => {
      state.snAvailabilityList = initialState.snAvailabilityList;
    },
    [checkSnAssignAvailability.rejected]: (state) => {
      state.snAvailabilityList.isError = true;
    },
    [checkSnAssignAvailability.fulfilled]: (state, action) => {
      const { payload } = action;

      state.snAvailabilityList.data = payload.data;
      state.snAvailabilityList.isForceReassign = payload.is_force_assign;
      state.snAvailabilityList.isError = false;
      state.snAvailabilityList.isSNexceedRange = payload.is_sn_exceed_range;
    },
    [validateUnassignUrl.pending]: (state) => {
      state.unassignV2Validation.isValidating = true;
      state.unassignV2Validation.isError = false;
    },
    [validateUnassignUrl.fulfilled]: (state, action) => {
      state.unassignV2Validation.isValidating = false;
      state.unassignV2Validation.urlResults = action.payload;
    },
    [validateUnassignUrl.rejected]: (state) => {
      state.unassignV2Validation.isError = true;
      state.unassignV2Validation.isValidating = false;
    },
    [validateUnassignRange.pending]: (state) => {
      state.unassignV2Validation.isValidating = true;
      state.unassignV2Validation.isError = false;
    },
    [validateUnassignRange.fulfilled]: (state, action) => {
      state.unassignV2Validation.isValidating = false;
      state.unassignV2Validation.rangeResults = action.payload;
    },
    [validateUnassignRange.rejected]: (state) => {
      state.unassignV2Validation.isError = true;
      state.unassignV2Validation.isValidating = false;
    },
    // Only a response makes the list "loaded". A rejected fetch leaves the flag alone, so the
    // panel keeps showing its skeleton rather than announcing that there are no bundles —
    // the middleware's error alert is what explains that case.
    [getBundleOptions.pending]: (state) => {
      state.bundleOptionsFailed = false;
    },
    [getBundleOptions.fulfilled]: (state, action) => {
      const { payload } = action;
      state.bundleOptions = payload;
      state.bundleOptionsLoaded = true;
      state.bundleOptionsFailed = false;
    },
    [getBundleOptions.rejected]: (state) => {
      state.bundleOptionsFailed = true;
    },
    // The last company ASKED for owns the slot. `companyFeatureId` is written from the very
    // same `meta.arg.id` the response is checked against, so the two are the identical value
    // — no coercion needed, and a late response for a company nobody is looking at any more
    // is dropped instead of overwriting the one on screen.
    //
    // Only a read for a DIFFERENT company blanks the slot. A re-read of the company already
    // on screen (the confirmation after a save, a retry) keeps the last known answer: the
    // panel renders a spinner whenever this is null, so blanking it turns every refresh into
    // a spinner — and a rejected refresh into a spinner that never comes back.
    [getCompanyFeature.pending]: (state, action) => {
      if (action.meta.arg.id !== state.companyFeatureId) {
        state.companyFeature = null;
      }
      state.companyFeatureId = action.meta.arg.id;
      state.companyFeatureError = null;
    },
    [getCompanyFeature.fulfilled]: (state, action) => {
      if (action.meta.arg.id !== state.companyFeatureId) {
        return;
      }
      state.companyFeature = action.payload;
      state.companyFeatureError = null;
    },
    // Record WHY rather than blanking the slot. With something already in it the panel keeps
    // showing the truth (setCompanyBundle below has already corrected it for a save that
    // succeeded); with nothing in it the panel has an explanation and a Retry to offer
    // instead of an endless spinner.
    [getCompanyFeature.rejected]: (state, action) => {
      if (action.meta.arg.id !== state.companyFeatureId) {
        return;
      }
      const reason = typeof action.payload === "string" ? action.payload.trim() : "";
      state.companyFeatureError = reason
        ? `Could not load this company's bundle: ${reason}`
        : "Could not load this company's bundle.";
    },
    // The write succeeded, so the company IS on that bundle now — say so at once instead of
    // waiting on the confirming re-read, which may fail. Without this, a save that worked
    // followed by a failed re-read leaves the panel describing the bundle the company held
    // BEFORE the save. Guarded by the same slot claim as the read: a response for a company
    // nobody is looking at any more may not touch the panel.
    [setCompanyBundle.fulfilled]: (state, action) => {
      if (action.meta.arg.id !== state.companyFeatureId || !state.companyFeature) {
        return;
      }
      state.companyFeature = {
        ...state.companyFeature,
        bundleId: action.meta.arg.bundleId,
      };
    },
    // A bundle that has just been created is live, whatever a cache fetched before it says.
    // bundle-options is refreshed after a create, but that refresh can fail — and then every
    // surface reading this list would caption the bundle the admin was just put on as "no
    // longer active" and draw its modules as none. Merging the create's own answer means the
    // list can be stale but never WRONG about a bundle this session made.
    [createBundle.fulfilled]: (state, action) => {
      const created = action.payload;
      if (!created || created.id === undefined || created.id === null) {
        return;
      }
      if (findBundleById(state.bundleOptions, created.id)) {
        return;
      }
      const requested = action.meta.arg || {};
      state.bundleOptions = [
        ...state.bundleOptions,
        {
          id: created.id,
          name: created.name || requested.name,
          // The request's module set is what the server was asked to store, so it is the
          // right answer even when the create response carries only an id.
          moduleIds: (created.moduleIds || requested.moduleIds || []).slice(),
        },
      ];
    },
  },
});

export const {
  clearCompanies,
  addBranchWhiteLabelCount,
  minusBranchWhiteLabelCount,
  resetBranchDropdown,
  resetProductDropdown,
  setReassignResult,
  clearReassignResult,
  resetSNAvailabilityList,
  resetUnassignV2Validation,
  clearCompanyFeature,
} = companiesSlice.actions;
export const companiesReducer = companiesSlice.reducer;
